import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SponsorService from "../../core/service/SponserService";
import EventService from "../../core/service/EventService";
import {
  formatEventDisplayDate,
  getEventSortDate,
  isEventUpcoming,
} from "../../utils/eventDateUtils";
import chennaiImage from "../../assets/CheImg.png";
import bangaloreImage from "../../assets/BangImg.png";
import hyderabadImage from "../../assets/HydImg.png";
import puneImage from "../../assets/puneImg.png";
import defaultCityImage from "../../assets/puneImg.png";
import "./SponsorServicePage.css";

const SponsorServicePage = () => {
  const navigate = useNavigate();

  const sponsorService = useMemo(() => new SponsorService(), []);
  const eventService = useMemo(() => new EventService(), []);

  const [eventDetails, setEventDetails] = useState(null);
  const [eventToken, setEventToken] = useState(null);

  const [formData, setFormData] = useState({
    companyName: "",
    type: "",
    noOfPasses: "",
    companyCode: "",
  });

  const [matchingCompanies, setMatchingCompanies] = useState([]);
  const [availablePasses, setAvailablePasses] = useState(0);
  const [isValidCompany, setIsValidCompany] = useState(false);

  const [passType, setPassType] = useState("Sponsors Passes");

  const getCityImage = (cityName) => {
    if (!cityName) return defaultCityImage;

    const lowerCityName = cityName.toLowerCase();

    switch (lowerCityName) {
      case "chennai":
        return chennaiImage;
      case "bangalore":
      case "bengaluru":
        return bangaloreImage;
      case "hyderabad":
        return hyderabadImage;
      case "pune":
        return puneImage;
      default:
        return defaultCityImage;
    }
  };

    useEffect(() => {
    const loadEvent = async () => {
      try {
        const res = await eventService.getAllEvents();

        if (res.data.responseCode !== 200) {
          toast.error("Failed to load events");
          return;
        }

        const events = res.data.responseBody;

        if (!events || events.length === 0) {
          toast.error("No events found");
          return;
        }

        const sorted = [...events].sort(
          (a, b) => getEventSortDate(a) - getEventSortDate(b)
        );

        const upcoming = sorted.filter((event) => isEventUpcoming(event));

        const selected = upcoming.length > 0 ? upcoming[0] : sorted[0];

        const token = selected.token || selected.euid;
        if (!token) {
          console.error(
            "Fetched event object is missing token property!",
            selected
          );
          toast.error(
            "Invalid event data: The event token is missing. Please contact support."
          );
          return;
        }

        setEventDetails(selected);
        setEventToken(token);
        localStorage.setItem("eventToken", token);
      } catch (e) {
        toast.error("Event loading failed");
      }
    };

    loadEvent();
  }, [eventService]);

  useEffect(() => {
    if (
      !formData.companyName ||
      formData.companyName.length < 2 ||
      !eventToken
    ) {
      setMatchingCompanies([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const res = await sponsorService.searchCompanies(
          formData.companyName,
          eventToken
        );

        if (res.data.responseCode === 200) {
          setMatchingCompanies(res.data.responseBody);
        } else {
          setMatchingCompanies([]);
        }
      } catch {
        setMatchingCompanies([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.companyName, eventToken]);

  useEffect(() => {
    if (!formData.companyName || formData.companyName.length < 3 || !eventToken)
      return;

    const timer = setTimeout(async () => {
      try {
        const res = await sponsorService.fetchCardPasses(
          formData.companyName,
          eventToken
        );

        if (res.data.responseCode === 200) {
          const body = res.data.responseBody;

          setFormData((prev) => ({
            ...prev,
            type: body.type,
            noOfPasses: "",
          }));

          setAvailablePasses(body.availablePasses || 0);
          setIsValidCompany(true);
        } else {
          setIsValidCompany(false);
          setAvailablePasses(0);
        }
      } catch {
        setIsValidCompany(false);
        setAvailablePasses(0);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.companyName, eventToken]);

  const chooseCompany = (name) => {
    setFormData({
      ...formData,
      companyName: name,
    });
    setMatchingCompanies([]);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    if (e.target.name === "companyName") {
      setIsValidCompany(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isValidCompany) {
      toast.error("Please select a valid company");
      return;
    }

    // Check if no of passes is zero or empty
    if (!formData.noOfPasses || Number(formData.noOfPasses) === 0) {
      toast.error("Please select at least one pass");
      return;
    }

    const payload = {
      companyName: formData.companyName,
      type: formData.type,
      noOfPasses: Number(formData.noOfPasses),
      companyCode: formData.companyCode,
    };

    try {
      const res = await sponsorService.registerSponsor(payload, eventToken);

      if (res.data.responseCode === 201) {
        navigate("/attendee-details", {
          state: {
            sponsorId: res.data.responseBody.sponsorId,
            eventDetails,
            noOfPasses: Number(formData.noOfPasses),
            eventToken: eventToken,
            sponsorData: {
              sponsorId: res.data.responseBody.sponsorId,
              companyName: formData.companyName,
              type: formData.type,
              companyCode: formData.companyCode,
            },
          },
        });
      } else {
        toast.error(res.data.responseMessage);
      }
    } catch {
      toast.error("Failed to register sponsor");
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <h1 className="register-title">Sponsor Registration</h1>

        {eventDetails && (
          <div className="event-card">
            <div className="event-image">
              <img
                src={getCityImage(eventDetails.city)}
                alt={eventDetails.city}
                className="event-image-img"
              />
            </div>
            <div className="event-info">
              <h2 className="event-city">
              {eventDetails?.city} | {formatEventDisplayDate(eventDetails)}
              </h2>
            </div>
          </div>
        )}

        {!eventToken && (
          <p style={{ color: "red", fontWeight: "bold" }}>
            Event Token Missing!
          </p>
        )}

        {/* Pass Type Selection */}
        <div className="pass-type-selection">
          <div className="radio-option">
            <input
              type="radio"
              id="sponsors-passes"
              name="passType"
              value="Sponsors Passes"
              checked={passType === "Sponsors Passes"}
              onChange={() => setPassType("Sponsors Passes")}
            />
            <label htmlFor="sponsors-passes">Sponsors Passes</label>
          </div>
        </div>
        <form className="sponsor-form" onSubmit={handleSubmit}>
          {/* COMPANY NAME */}
          <div className="form-group company-autocomplete-container">
            <input
              type="text"
              name="companyName"
              placeholder="Company Name*"
              className="form-input"
              value={formData.companyName}
              onChange={handleChange}
            />

            {/* AUTOCOMPLETE */}
            {matchingCompanies.length > 0 && (
              <div className="company-dropdown">
                {matchingCompanies.map((c, i) => (
                  <div
                    key={i}
                    className="company-option"
                    onClick={() => chooseCompany(c)}
                  >
                    {c}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* PASS TYPE */}
          <input
            type="text"
            name="type"
            className="form-input"
            placeholder="Pass Type"
            value={formData.type}
            readOnly
          />

          {/* PASS COUNT */}
          <select
            name="noOfPasses"
            className="form-select"
            value={formData.noOfPasses}
            onChange={handleChange}
            disabled={!isValidCompany}
          >
            <option value="">No of passes</option>
            {Array.from({ length: availablePasses }, (_, i) => i + 1).map(
              (num) => (
                <option key={num} value={num}>
                  {num}
                </option>
              )
            )}
          </select>

          {/* COMPANY CODE */}
          {/* <input
            type="text"
            name="companyCode"
            placeholder="Company Code"
            className="form-input"
            value={formData.companyCode}
            onChange={handleChange}
          /> */}

          <button type="submit" className="form-submit">
            Continue
          </button>
        </form>
      </div>
    </div>
  );
};

export default SponsorServicePage;
